/**
 * When a session must be refreshed.
 *
 * Expiry used to be tested as "already past" — `expires_at < now` — which
 * hands out a token with a second left and lets it expire while the request
 * travels. Measured repeatedly on the appliance 2026-08-24: `/auth/me`
 * answered 200 and moments later the same session 401'd with no refresh in
 * between, and a reload dropped the user to the login page. The refresh
 * machinery was there and working; it was simply never asked.
 *
 * Two things widen that window. `expires_at` is computed at login on the
 * CLIENT clock (`now + expires_in`), so any skew against the server shifts it;
 * and a single page holds the value across many requests, so the last one
 * before expiry is the one that fails.
 */

/**
 * How long before expiry a session is already treated as expired.
 *
 * Big enough to cover a round trip plus modest clock skew, small enough that a
 * 15-minute token is not refreshed on every call — a margin that swallowed the
 * lifetime would make the identity service a hot path.
 */
export const SESSION_REFRESH_MARGIN_SECONDS = 60

export function sessionNeedsRefresh(
  session: { expires_at?: number | null } | null | undefined,
  nowSeconds: number = Math.floor(Date.now() / 1000),
): boolean {
  const expiresAt = session?.expires_at
  // Unknown is not the same as expired. Refusing here would sign out every
  // session whose provider omits the field.
  if (typeof expiresAt !== "number" || !Number.isFinite(expiresAt)) return false
  return expiresAt - SESSION_REFRESH_MARGIN_SECONDS <= nowSeconds
}
